import React, { useState, useEffect, useCallback, useMemo } from 'react'; 
import styled from 'styled-components';
import { 
  IoCheckmarkCircle, 
  IoLocationOutline, 
  IoTimeOutline,
  IoPersonOutline,
  IoDocumentTextOutline,
  IoAlertCircleOutline
} from 'react-icons/io5';
import { IconButton } from '../../UI/Buttons'; 
import { Text } from '../../UI/Typography';
import Table from '../../UI/Table';
import EditTimesModal from './EditTimesModal';
import NotesModal from './NotesModal'; 
import LocationMapModal from './LocationMapModal';
import PropTypes from 'prop-types';

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme, variant }) => 
    variant === 'status' ? theme.borderRadius.full : theme.borderRadius.medium};
  font-size: 13px;
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: ${({ clickable }) => clickable ? 'pointer' : 'default'};
  transition: all 0.2s ease;
  
  ${({ theme, type, clickable }) => {
    const colorMap = {
      // attendance status mappings
      present: theme.colors.success,
      late: '#f55d0b',
      leave: theme.colors.primary,
      'half-day': theme.colors.primary,
      absent: theme.colors.error,
      // other UI types
      duration: theme.colors.text.primary,
      location: theme.colors.primary,
      default: theme.colors.text.secondary
    };
    
    const color = colorMap[type] || colorMap.default;
    const isDuration = type === 'duration';
    
    // Build background and border with a translucent variant when possible
    const bg = isDuration ? (theme.colors.backgroundSecondary || theme.colors.background) : `${color}20`;
    const borderColor = isDuration ? `${theme.colors.border}30` : `${color}30`;

    return `
      background-color: ${bg};
      color: ${color};
      border: 1px solid ${borderColor};
      ${isDuration ? `font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;` : ''}
      
      ${clickable ? `
        &:hover {
          background-color: ${isDuration ? 
            theme.colors.backgroundSecondary || theme.colors.background : 
            `${color}28`};
          border-color: ${`${color}40`};
          transform: translateY(-1px);
          box-shadow: ${theme.shadows.small};
        }
      ` : ''}
    `;
  }}
  
  ${({ isOngoing, theme }) => isOngoing && `
    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
    background-color: ${theme.colors.success}08;
    border-color: ${theme.colors.success}30;
    color: ${theme.colors.success};
    
    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.7; }
    }
  `}
`;

const ActionButton = styled(IconButton)`
  color: ${({ theme }) => theme.colors.success};
  font-size: ${({ theme }) => theme.typography.fontSizes.lg};
  width: 40px;
  height: 40px;
  border-radius: 50%;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover:not(:disabled) {
    background-color: ${({ theme }) => `${theme.colors.success}15`};
    transform: scale(1.1);
    box-shadow: ${({ theme }) => theme.shadows.medium};
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

// Constants
const COLUMNS = [
  { key: 'full_name', label: 'Full Name', icon: IoPersonOutline },
  { key: 'in_time', label: 'In Time', icon: IoTimeOutline, width: '50px' },
  { key: 'in_location', label: 'In Location', icon: IoLocationOutline },
  { key: 'out_time', label: 'Out Time', icon: IoTimeOutline, width: '50px' },
  { key: 'out_location', label: 'Out Location', icon: IoLocationOutline },
  { key: 'duration', label: 'Duration', icon: IoTimeOutline},
  { key: 'accepted', label: 'Status', icon: IoCheckmarkCircle },
  { key: 'actions', label: 'Actions', icon: null, align: 'center' }
];

const STATUS_ICONS = {
  pending: IoAlertCircleOutline,
  accepted: IoCheckmarkCircle,
  rejected: IoAlertCircleOutline
};

// Utility Functions
const calculateDuration = (date, inTime, outTime) => {
  if (!date || !inTime) return { display: 'N/A', isOngoing: false };

  try {
    const startTime = new Date(`${date}T${inTime}`);
    if (isNaN(startTime.getTime())) return { display: 'Invalid', isOngoing: false };

    const isOngoing = !outTime;
    const endTime = outTime ? new Date(`${date}T${outTime}`) : new Date();
    
    if (isNaN(endTime.getTime()) || endTime < startTime) {
      return { display: 'Error', isOngoing: false };
    }

    const hours = Math.floor((endTime - startTime) / 3600000);
    const minutes = Math.floor(((endTime - startTime) % 3600000) / 60000);
    const display = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    
    return { display: isOngoing ? `${display} (ongoing)` : display, isOngoing };
  } catch {
    return { display: 'Error', isOngoing: false };
  }
};

// Map attendance status to icons
const STATUS_ICON_MAP = {
  present: IoCheckmarkCircle,
  late: IoAlertCircleOutline,
  leave: IoDocumentTextOutline,
  'half-day': IoDocumentTextOutline,
  absent: IoAlertCircleOutline,
  // fallback
  default: IoAlertCircleOutline
};

const CompactTable = styled(Table)`
  th, td {
    padding: 8px  !important; /* Reduced from default padding */
    font-size: 14px;
  }
  th {
    padding: 15px !important; /* Slightly more padding for headers */
    font-size: 14px;
  }
`;
// Custom Empty State Component
const AttendanceEmptyState = () => (
  <div style={{ textAlign: 'center', padding: '48px', color: '#6b7280' }}>
    <IoDocumentTextOutline style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }} />
    <Text size="lg" weight="medium" style={{ margin: '0 0 8px 0' }}>
      No Attendance Data
    </Text>
    <Text size="sm" style={{ margin: 0, opacity: 0.7 }}>
      No attendance records found for the selected date.
    </Text>
  </div>
);

// Main Component
const AttendanceTable = ({ 
  data = [], 
  sites = [], 
  onNotesSubmit,
  onEditTimesSave,
  isLoading = false,
  error = null
}) => { 
  const [modals, setModals] = useState({
    notes: { isOpen: false, item: null },
    map: { isOpen: false, data: null },
    editTimes: { isOpen: false, item: null }
  });
  // Removed unused 'tick' state to resolve linter warning

  // Effects
  useEffect(() => {
    const hasOngoingEntries = data.some(item => item.in_time && !item.out_time);
    if (!hasOngoingEntries) return;

    // This effect previously updated 'tick' to force re-render for ongoing durations.
    // If you want to keep live updates, you can use a forceUpdate pattern or similar.
    // For now, removed to resolve unused variable warning.
  }, [data]);

  // Memoized data processing
  const processedData = useMemo(() => {
    // Group items so order is: present/late -> leave/half-day -> absent
    const groups = { 0: [], 1: [], 2: [] };
    const rank = (status) => {
      if (status === 'absent') return 2;
      if (status === 'leave' || status === 'half-day') return 1;
      return 0; // present, late, others
    };

    const sortByInTime = (a, b) => {
      const ta = a.in_time;
      const tb = b.in_time;
      if (ta && tb) return ta.localeCompare(tb);
      if (ta && !tb) return -1; // a comes before b
      if (!ta && tb) return 1; // b comes before a
      // both missing - fallback to username
      const ua = (a.username || '').toLowerCase();
      const ub = (b.username || '').toLowerCase();
      return ua.localeCompare(ub);
    };

    data.forEach(item => {
      const itm = { ...item, icon: STATUS_ICON_MAP[item.status] || STATUS_ICON_MAP.default };
      groups[rank(item.status)].push(itm);
    });

    // sort each group by in_time ascending (nulls last), then username
    Object.keys(groups).forEach(k => groups[k].sort(sortByInTime));

    return [...groups[0], ...groups[1], ...groups[2]];
  }, [data]);

  // Event handlers
  const modalHandlers = useMemo(() => ({
    openNotes: (item) => setModals(prev => ({ ...prev, notes: { isOpen: true, item } })),
    closeNotes: () => setModals(prev => ({ ...prev, notes: { isOpen: false, item: null } })),
    openMap: (lat, lng, label, locationType, locationName) => {
      const siteName = locationType === 'Site' ? locationName : undefined;
      setModals(prev => ({ 
        ...prev, 
        map: { isOpen: true, data: { lat, lng, label, siteName } }
      }));
    },
    closeMap: () => setModals(prev => ({ ...prev, map: { isOpen: false, data: null } }))
    ,
    openEditTimes: (item) => setModals(prev => ({ ...prev, editTimes: { isOpen: true, item } })),
    closeEditTimes: () => setModals(prev => ({ ...prev, editTimes: { isOpen: false, item: null } })),
    savedEditTimes: (item) => setModals(prev => ({ ...prev, editTimes: { isOpen: false, item: null } }))
  }), []);

  const handleNotesSubmit = useCallback((itemId, notes) => {
    onNotesSubmit?.(itemId, notes);
    modalHandlers.closeNotes();
  }, [onNotesSubmit, modalHandlers]);

  // Get row status for styling
  const getRowStatus = useCallback((item) => {
    return item.status || 'default';
  }, []);

  // Cell renderer
  const renderCell = useCallback((item, column) => {
    const value = item[column.key];

    switch (column.key) {
      case 'duration':
        const duration = calculateDuration(item.date, item.in_time, item.out_time);
        return (
          <Badge type="duration" isOngoing={duration.isOngoing}>
            <IoTimeOutline size={14} />
            {duration.display}
          </Badge>
        );

      case 'accepted':
        const IconComponent = item.icon;
        return (
          <Badge type={item.status} variant="status">
            <IconComponent size={14} />
            {item.status}
          </Badge>
        );



      case 'full_name':
        const userFullName = [item.first_name, item.last_name].filter(Boolean).join(' ');
        return (
          <Text
            size="sm"
            style={{
              margin: 0,
              fontWeight: '500',
              color: userFullName ? 'inherit' : '#6b7280'
            }}
            title={userFullName || item.username || value || 'No user information'}
          >
            {userFullName || item.username || value || 'N/A'}
          </Text>
        );

      case 'in_location':
      case 'out_location':
        const isIn = column.key === 'in_location';
        const lat = item[isIn ? 'in_latitude' : 'out_latitude'];
        const lng = item[isIn ? 'in_longitude' : 'out_longitude'];
        const locationType = item[isIn ? 'in_location_type' : 'out_location_type'];
        
        if (lat && lng) {
          return (
            <Badge 
              type="location" 
              clickable
              onClick={() => modalHandlers.openMap(lat, lng, value || `${isIn ? 'In' : 'Out'} Location`, locationType, value)}
              title={`View ${isIn ? 'In' : 'Out'} Location (${lat}, ${lng})`}
            >
              <IoLocationOutline size={14} />
              {value || 'View Location'}
            </Badge>
          );
        }
        return value || 'N/A';

      case 'actions':
        // Render an edit button (left empty for now)
        const EditIcon = IoDocumentTextOutline; // reuse an icon for edit; keep action empty for now
        return (
          <ActionButton
            title="Edit"
            onClick={() => modalHandlers.openEditTimes(item)}
            aria-label="Edit attendance"
          >
            <EditIcon size={18} />
          </ActionButton>
        );

      default:
        return value || 'N/A';
    }
  }, [modalHandlers]);

  return (
    <>
      <CompactTable
        columns={COLUMNS}
        data={processedData}
        renderCell={renderCell}
        getRowStatus={getRowStatus}
        isLoading={isLoading}
        error={error}
        emptyState={<AttendanceEmptyState />}
        hoverable={true}
      />

      <NotesModal 
        isOpen={modals.notes.isOpen}
        onClose={modalHandlers.closeNotes}
        onSubmit={handleNotesSubmit}
        item={modals.notes.item}
      />

      <EditTimesModal
        isOpen={modals.editTimes.isOpen}
        onClose={modalHandlers.closeEditTimes}
        onSave={async (payload) => {
          // Call parent handler if provided
          if (typeof onEditTimesSave === 'function') {
            await onEditTimesSave(payload);
          }
          modalHandlers.savedEditTimes();
        }}
        item={modals.editTimes.item}
      />

      <LocationMapModal
        isOpen={modals.map.isOpen}
        onClose={modalHandlers.closeMap}
        locationData={modals.map.data}
        sites={sites}
      />
    </>
  );
};

AttendanceTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  sites: PropTypes.arrayOf(PropTypes.object),
  onNotesSubmit: PropTypes.func,
  onEditTimesSave: PropTypes.func,
  isLoading: PropTypes.bool,
  error: PropTypes.string
};

export default React.memo(AttendanceTable);
